import React, { CSSProperties, ReactNode } from 'react'

import { Box } from '@gousto-internal/citrus-react'

import { RevealTrigger, useReveal } from './useReveal'

// The one canonical "Appearance animation": fade + rise. Defined ONCE here and reused everywhere via
// composition, rather than re-tuned per section. Values are ticket-specified (B20-1258): opacity
// 0→1, translateY(30px)→none, cubic-bezier(.2,.7,.2,1). px literal per repo sizing guidance. Duration
// tuned to 1.6s and rise to 30px during review — the original 0.8s/16px read as too quick and subtle;
// 30px matches Apple's larger fade+rise displacement.
const APPEARANCE_DURATION = '1.6s'
const APPEARANCE_EASING = 'cubic-bezier(0.2, 0.7, 0.2, 1)'
const APPEARANCE_RISE = '30px'
const APPEARANCE_TRANSITION = `opacity ${APPEARANCE_DURATION} ${APPEARANCE_EASING}, transform ${APPEARANCE_DURATION} ${APPEARANCE_EASING}`

export type CWCAppearanceProps = {
  children: ReactNode
  /**
   * When the element enters. `'scroll'` (default) reveals the first time it scrolls into view;
   * `'load'` reveals on mount, for above-the-fold elements where there is nothing to scroll to.
   */
  trigger?: RevealTrigger
  /**
   * `display` for the wrapper. Defaults to block. Pass e.g. `'inline-flex'` when wrapping an inline
   * element (such as the Hero badge sitting inline in the eyebrow) so the wrapper does not introduce
   * a line box / descender gap that would shift the element's alignment.
   */
  display?: CSSProperties['display']
}

/**
 * Wraps its children so they enter with the canonical Appearance animation (fade + rise), once. The
 * reveal is single-shot and show-once — it never re-hides on scroll back — and honours
 * `prefers-reduced-motion` and no-JS / no-`IntersectionObserver` environments (see `useReveal`).
 *
 * The reveal ref is attached to a plain Citrus `Box` (which forwards refs to the DOM), matching how
 * `AboveTheFoldSection` / `useAutoplayOnIntersect` observe elements — `withCitrusTheme` wrappers do
 * not forward a ref, so the observed node must be a plain element. Only `opacity`/`transform`
 * animate (GPU-composited, no layout reflow); they are toggled per the runtime `isRevealed` state,
 * which is the sanctioned case for inline style. Revealed is the resting state, so SSR, no-JS and
 * reduced-motion all render the final, visible state.
 */
export function CWCAppearance({ children, trigger = 'scroll', display }: CWCAppearanceProps) {
  const { ref, isRevealed, immediate } = useReveal<HTMLDivElement>({ trigger })

  const style: CSSProperties = {
    opacity: isRevealed ? 1 : 0,
    transform: isRevealed ? 'none' : `translateY(${APPEARANCE_RISE})`,
    // `immediate` (reduced motion) shows the end state with no animation.
    transition: immediate ? 'none' : APPEARANCE_TRANSITION,
    willChange: 'opacity, transform',
    ...(display ? { display } : {}),
  }

  return (
    <Box ref={ref} data-revealed={isRevealed ? 'true' : 'false'} style={style}>
      {children}
    </Box>
  )
}
